// @ts-check
import { createLog } from '../utils/logger'
import { TCString } from '@mol-ads/tcf-core'
import { getVendorListsAndMigrationVersions } from './vendorListVersions'
import { publisherRestrictionPurposeIds as defaultPublisherRestrictionPurposeIds } from './publisherRestrictions'

const log = createLog()

/**
 * @template T
 */
export class Config {
  /**
   * @type {T}
   */
  #config
  /**
   * @param {T} config
   */
  constructor(config) {
    this.#config = config
  }

  /**
   * @template {keyof T} K
   * @overload
   * @param {K} key
   * @return {T[K]|void}
   */
  /**
   * @overload
   * @return {T}
   */
  /**
   * @template {keyof T} K
   * @param {K} [key]
   * @return {T[K]|T|void}
   */
  get(key) {
    if (!key) return this.#config

    if (Object.prototype.hasOwnProperty.call(this.#config, key)) {
      return this.#config[key]
    }
  }
}

/**
 * @param {ClientConfig} clientConfig
 * @return {CmpFromClientConfig}
 */
function getCmpConfigFromClientConfig(clientConfig) {
  const {
    backendUrl,
    brand,
    brokerId,
    controllerScriptBase,
    renderPlatform,
    showUi,
    uiLevel,
    uiScriptUrl,
    uuid,
    platform,
    publisherRestrictions
  } = clientConfig

  const publisherConsents = {
    purposes: brand?.primaryVendor.purposes || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    legIntPurposes: brand?.primaryVendor.legIntPurposes || [5, 6, 8, 10]
  }

  return {
    backendUrl,
    brand,
    brokerId,
    controllerScriptBase,
    renderPlatform,
    showUi,
    uiLevel,
    uiScriptUrl,
    uuid,
    platform,
    publisherRestrictions:
      publisherRestrictions || defaultPublisherRestrictionPurposeIds,
    publisherConsents
  }
}

/**
 * @param {ClientConfig} clientConfig
 * @param {TCData|undefined} tcDataFromStorage
 * @return {{nextRepromptVersion: number, repromptVersion: number}}
 */
function selectRepromptVersions(clientConfig, tcDataFromStorage) {
  const {
    repromptVersion: currentRepromptVersionFromClient,
    nextRepromptVersion: nextRepromptVersionFromClient
  } = clientConfig
  const { repromptVersion: repromptVersionFromStorage = 0 } =
    tcDataFromStorage || {}
  const repromptVersion = Math.max(
    currentRepromptVersionFromClient,
    repromptVersionFromStorage
  )
  const nextRepromptVersion = Math.max(
    repromptVersion,
    nextRepromptVersionFromClient
  )
  log('nextRepromptVersion', nextRepromptVersion)
  return { nextRepromptVersion, repromptVersion }
}

/**
 * @param {ClientConfig} clientConfig
 * @param {TCData|undefined} tcDataFromStorage
 * @return {OverrideableByStoredConfigItems}
 */
function selectConfigBasedOnTCDataFromLocalStorage(
  clientConfig,
  tcDataFromStorage
) {
  const {
    tcString: tcStringFromStorage = undefined,
    nrvString: nrvStringFromStorage = undefined,
    nrvVersion: nrvVersionFromStorage = undefined,
    addtlConsent: addtlConsentFromStorage = undefined,
    acmVersion: cvlVersionFromStorage,
    molGvlVersion: molGvlVersionFromStorage = undefined
  } = tcDataFromStorage || {}

  const {
    tcString: tcStringFromClient,
    addtlConsent: addtlConsentFromClient,
    cvlVersion: cvlVersionFromClient,
    nrvString: nrvStringFromClient,
    nrvVersion: nrvVersionFromClient,
    molGvlVersion: molGvlVersionFromClient
  } = clientConfig || {}

  const repromptVersions = selectRepromptVersions(
    clientConfig,
    tcDataFromStorage
  )

  const configFromStorage = {
    tcString: tcStringFromStorage,
    nrvString: nrvStringFromStorage,
    nrvVersion: nrvVersionFromStorage,
    addtlConsent: addtlConsentFromStorage,
    cvlVersion: cvlVersionFromStorage,
    molGvlVersion: molGvlVersionFromStorage,
    ...repromptVersions
  }
  const configFromClient = {
    tcString: tcStringFromClient,
    nrvString: nrvStringFromClient,
    nrvVersion: nrvVersionFromClient,
    addtlConsent: addtlConsentFromClient,
    cvlVersion: cvlVersionFromClient,
    molGvlVersion: molGvlVersionFromClient,
    ...repromptVersions
  }
  log('clientString', tcStringFromClient)
  log('storedString', tcStringFromStorage)
  const selectStrings = (tcStringFromStorage, tcStringFromClient) => {
    const stored = TCString.decode(tcStringFromStorage).lastUpdated
    const client = TCString.decode(tcStringFromClient).lastUpdated
    log('stored', stored)
    log('client', client)
    return stored > client ? configFromStorage : configFromClient
  }

  if (tcStringFromClient && tcStringFromStorage) {
    return selectStrings(tcStringFromStorage, tcStringFromClient)
  } else if (tcStringFromClient) {
    return configFromClient
  } else {
    return configFromStorage
  }
}

/**
 * @param {Config<ClientConfig>} config
 * @param {TCData} [tcDataFromLocalStorage]
 * @return {Config<CMPConfig>}
 */
export function createCmpConfig(config, tcDataFromLocalStorage) {
  const clientConfig = config.get()
  const selectedTCDataItems = selectConfigBasedOnTCDataFromLocalStorage(
    clientConfig,
    tcDataFromLocalStorage
  )

  const {
    addtlConsent,
    nrvString,
    nextRepromptVersion,
    repromptVersion,
    tcString
  } = selectedTCDataItems

  const existingUser = !!clientConfig.tcString

  const {
    gvlVersion,
    gvlUiVersion,
    migratedGvlVersion,
    migrateUserGvl,
    ...nonTCFVendorListVersions
  } = getVendorListsAndMigrationVersions(
    selectedTCDataItems,
    clientConfig,
    existingUser
  )

  /**
   * @type {Config<CMPConfig>}
   */
  return new Config({
    ...getCmpConfigFromClientConfig(clientConfig),
    addtlConsent,
    tcString,
    nrvString,
    nextRepromptVersion,
    repromptVersion,
    ...nonTCFVendorListVersions,
    gvlVersion,
    gvlUiVersion,
    migratedGvlVersion,
    existingUser,
    migrateUserGvl
  })
}

/**
 * @template {ClientConfig} T
 * @param {T} config
 * @return {Config<T>}
 */
export function createClientConfig(config) {
  log('config', config)
  return new Config({ cmpModel: 'tcfv2', ...config })
}

/**
 * @typedef {CmpFromClientConfig &
 * Pick<OverrideableByStoredConfigItems,'tcString'|'addtlConsent'|'nrvString'|'nextRepromptVersion'|'repromptVersion'> &
 * Pick<VendorListVersions,'nrvVersion'|'nrvUiVersion'|'cvlVersion'|'cvlUiVersion'> &
 * {gvlVersion: string|number, gvlUiVersion: string|number, migratedGvlVersion: string|undefined, existingUser: boolean, migrateUserGvl: boolean, publisherConsents: PublisherConsents}
 * } CMPConfig
 */

/**
 * @typedef {Object} VendorListVersions
 * @property {number} gvlVersion
 * @property {number} gvlUiVersion
 * @property {number} nrvVersion
 * @property {number} nrvUiVersion
 * @property {string|number} cvlVersion
 * @property {number} cvlUiVersion
 */

/**
 * @typedef {Omit<ClientConfig,'storeUserPreference'|'useGeoDeviceVariantGVL'|'geo'|'gvlEnvironment'> & {migratedGvlVersion: string|undefined, existingUser: boolean, migrateUserGvl: boolean, gvlUiVersion: string}} _CMPConfig
 */

/**
 * @typedef {Object} ClientConfig
 * @property {string} [addtlConsent]
 * @property {string} [backendUrl]
 * @property {Brand} [brand]
 * @property {string} [brokerId]
 * @property {string} [channel]
 * @property {string} controllerScriptBase
 * @property {string|number} cvlVersion
 * @property {string|number} cvlUiVersion
 * @property {string} [gvlEnvironment]
 * @property {string|number} gvlUiVersion
 * @property {string|number} gvlVersion
 * @property {number} nextRepromptVersion
 * @property {'mobile'|'desktop'} [renderPlatform]
 * @property {number} repromptVersion
 * @property {boolean|string} [showUi]
 * @property {boolean} [storeUserPreference]
 * @property {string} [tcString]
 * @property {UILevel} [uiLevel]
 * @property {string} uiScriptUrl
 * @property {string} [uuid]
 * @property {string} platform
 * @property {string} [nrvString]
 * @property {string} nrvUiVersion
 * @property {string|number} nrvVersion
 * @property {boolean} [useGeoDeviceVariantGVL]
 * @property {string} [molGvlVersion]
 * @property {string} [geo]
 * @property {'tcfv2'|'lgpd'} [cmpModel]
 * @property {Array<number>} [publisherRestrictions]
 * @property {boolean} [consentOrPay]
 * @property {string} [consentOrPaySubscriptionURL]
 * @property {boolean} [dissentUser]
 * @property {boolean} [isUserLoggedIn]
 * @property {string} [domain]
 * @property {boolean} [isMailEssentialSubscriber]
 * @property {Array<string>} [persistTags]
 */

/**
 * @typedef {'Level1' | 'Level2'} UILevel
 */

/**
 * @typedef {import('@mol-ads/tcf-cmpapi').TCData} TCData
 */

/**
 * @typedef {Object} OverrideableByStoredConfigItems
 * @property {string|undefined} tcString
 * @property {string|undefined} addtlConsent
 * @property {string|number|undefined} cvlVersion
 * @property {string|undefined} nrvString
 * @property {string|number|undefined} nrvVersion
 * @property {string|undefined} molGvlVersion
 * @property {number} nextRepromptVersion
 * @property {number} repromptVersion
 */

/**
 * @typedef {Pick<ClientConfig,
 * 'backendUrl'|
 * 'brand'|
 * 'brokerId'|
 * 'controllerScriptBase'|
 * 'renderPlatform'|
 * 'showUi'|
 * 'uiLevel'|
 * 'uiScriptUrl'|
 * 'uuid'|
 * 'platform'|
 * 'publisherRestrictions'
 * > & {publisherConsents: PublisherConsents}} CmpFromClientConfig
 */

/**
 * @typedef {{level1Paragraphs: Array<string>, primaryVendor: PrimaryVendor}} Brand
 */

/**
 * @typedef {Object} PrimaryVendor
 * @property {Array<number>} dataDeclaration
 * @property {Array<number>} features
 * @property {Array<number>} flexiblePurposes
 * @property {string} headingName
 * @property {Array<number>} legIntPurposes
 * @property {string} name
 * @property {string} policyUrl
 * @property {Array<number>} purposes
 * @property {Array<number>} specialFeatures
 * @property {Array<number>} specialPurposes
 */

/**
 * @typedef {{purposes: Array<number>, legIntPurposes: Array<number>}} PublisherConsents
 */
